import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { MANAGER_PLUS_ROLES, OWNER_PLUS_ROLES, STAFF_ROLES } from '@repo/types'
import { AuthProvider, useAuth } from '@/contexts/AuthContext'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { LoginPage } from '@/pages/auth/LoginPage'
import { LazyFallback } from '@/components/ui/LazyFallback'
import { ConfirmProvider } from '@/components/ui/ConfirmDialog'

// Page chunks are code-split (L17) so the initial bundle stays small. LoginPage
// stays eager as the unauthenticated entry point; everything else loads on demand
// inside the layout's <Suspense> (sidebar stays visible during the chunk fetch).
// Named exports need the `.then(m => ({ default: m.X }))` shim React.lazy expects.
const ChangePasswordPage = lazy(() => import('@/pages/auth/ChangePasswordPage').then(m => ({ default: m.ChangePasswordPage })))
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })))
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })))
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage').then(m => ({ default: m.DashboardPage })))
const MyDealsPage = lazy(() => import('@/pages/deals/MyDealsPage').then(m => ({ default: m.MyDealsPage })))
const PropertiesPage = lazy(() => import('@/pages/properties/PropertiesPage').then(m => ({ default: m.PropertiesPage })))
const PropertyFormPage = lazy(() => import('@/pages/properties/PropertyFormPage').then(m => ({ default: m.PropertyFormPage })))
const DealDetailPage = lazy(() => import('@/pages/crm/DealDetailPage').then(m => ({ default: m.DealDetailPage })))
const PipelinePage = lazy(() => import('@/pages/crm/PipelinePage').then(m => ({ default: m.PipelinePage })))
const CalendarPage = lazy(() => import('@/pages/calendar/CalendarPage').then(m => ({ default: m.CalendarPage })))
const ReportsPage = lazy(() => import('@/pages/reports/ReportsPage').then(m => ({ default: m.ReportsPage })))
const UsersPage = lazy(() => import('@/pages/users/UsersPage').then(m => ({ default: m.UsersPage })))
const UserDetailPage = lazy(() => import('@/pages/users/UserDetailPage').then(m => ({ default: m.UserDetailPage })))
const BranchesPage = lazy(() => import('@/pages/branches/BranchesPage').then(m => ({ default: m.BranchesPage })))
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage').then(m => ({ default: m.SettingsPage })))
const ExportCenterPage = lazy(() => import('@/pages/exports/ExportCenterPage').then(m => ({ default: m.ExportCenterPage })))
const MediaLibraryPage = lazy(() => import('@/pages/media/MediaLibraryPage').then(m => ({ default: m.MediaLibraryPage })))
const EmailTemplatesListPage = lazy(() => import('@/pages/email-templates/EmailTemplatesListPage').then(m => ({ default: m.EmailTemplatesListPage })))
const EmailTemplateFormPage = lazy(() => import('@/pages/email-templates/EmailTemplateFormPage').then(m => ({ default: m.EmailTemplateFormPage })))
const AutomationPage = lazy(() => import('@/pages/automation/AutomationPage').then(m => ({ default: m.AutomationPage })))
const ImportPage = lazy(() => import('@/pages/imports/ImportPage').then(m => ({ default: m.ImportPage })))
const SuperAdminPage = lazy(() => import('@/pages/super-admin/SuperAdminPage').then(m => ({ default: m.SuperAdminPage })))
const MyTeamPage = lazy(() => import('@/pages/my-team/MyTeamPage').then(m => ({ default: m.MyTeamPage })))
const MyProfilePage = lazy(() => import('@/pages/profile/MyProfilePage').then(m => ({ default: m.MyProfilePage })))
const MyWebOverviewPage = lazy(() => import('@/pages/my-web/MyWebOverviewPage').then(m => ({ default: m.MyWebOverviewPage })))
const MyWebSettingsPage = lazy(() => import('@/pages/my-web/MyWebSettingsPage').then(m => ({ default: m.MyWebSettingsPage })))
const MyWebContentPage = lazy(() => import('@/pages/my-web/MyWebContentPage').then(m => ({ default: m.MyWebContentPage })))
const MyWebBlogPage = lazy(() => import('@/pages/my-web/MyWebBlogPage').then(m => ({ default: m.MyWebBlogPage })))
const MyWebBlogFormPage = lazy(() => import('@/pages/my-web/MyWebBlogFormPage').then(m => ({ default: m.MyWebBlogFormPage })))
const MyWebReferencesPage = lazy(() => import('@/pages/my-web/MyWebReferencesPage').then(m => ({ default: m.MyWebReferencesPage })))
const TasksPage = lazy(() => import('@/pages/tasks/TasksPage').then(m => ({ default: m.TasksPage })))
const MonitorPage = lazy(() => import('@/pages/monitor/MonitorPage').then(m => ({ default: m.MonitorPage })))
const ContactsPage = lazy(() => import('@/pages/crm/ContactsPage').then(m => ({ default: m.ContactsPage })))
const ContactDetailPage = lazy(() => import('@/pages/crm/contact-detail/ContactDetailPage').then(m => ({ default: m.ContactDetailPage })))
const EmailsPage = lazy(() => import('@/pages/email/EmailsPage').then(m => ({ default: m.EmailsPage })))
const NotificationsPage = lazy(() => import('@/pages/Notifications').then(m => ({ default: m.NotificationsPage })))

// Phase 4 — tiper/lawyer additions (default exports)
const TipersPage = lazy(() => import('@/pages/tipers/TipersPage'))
const TiperDetailPage = lazy(() => import('@/pages/tipers/TiperDetailPage'))
const LegalRequestsPage = lazy(() => import('@/pages/legal/LegalRequestsPage'))
const LegalRequestDetail = lazy(() => import('@/pages/legal/LegalRequestDetail'))
const PartyFormPage = lazy(() => import('@/pages/public/PartyFormPage'))
const TiperDashboard = lazy(() => import('@/pages/dashboard/TiperDashboard'))
const LawyerDashboard = lazy(() => import('@/pages/dashboard/LawyerDashboard'))

// Route-level role gating — mirrors Sidebar visibility. TIPER a LAWYER sú na API
// default-deny (RoleAllowlistGuard): un-gated stránka by im odpálila 403 queries
// a spurious „Nemáte oprávnenie" toast. Každá route preto deklaruje, kto ju smie
// mountnúť; RoleRoute ostatných presmeruje na /dashboard (rolovo-správny cez
// DashboardRouter). Pre bežné roly (ASSISTANT..OWNER/SUPER_ADMIN) sa nič nemení.
const STAFF_AND_TIPER = [...STAFF_ROLES, 'TIPER']
const STAFF_AND_LAWYER = [...STAFF_ROLES, 'LAWYER']

function DashboardRouter() {
  const { user } = useAuth()
  if (user?.role === 'TIPER') {
    return (
      <Suspense fallback={<LazyFallback />}>
        <TiperDashboard />
      </Suspense>
    )
  }
  if (user?.role === 'LAWYER') {
    return (
      <Suspense fallback={<LazyFallback />}>
        <LawyerDashboard />
      </Suspense>
    )
  }
  return <DashboardPage />
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return (
    <div className="flex items-center justify-center h-screen">
      <div className="animate-spin w-8 h-8 border-2 border-brand-600 border-t-transparent rounded-full" />
    </div>
  )
  if (!user) return <Navigate to="/login" replace />
  return <>{children}</>
}

function RoleRoute({ roles, children }: { roles: readonly string[]; children: React.ReactNode }) {
  const { user } = useAuth()
  if (!user || !roles.includes(user.role)) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

/**
 * Staré adresy kontaktov a zákazníkov (záložky, staré notifikácie, odkazy v e-mailoch) → `/crm/contacts`.
 * ID ostali pri zlúčení rovnaké; filter makléra sa v zozname volá `agentId`. Filter `customerType`
 * zoznamu zákazníkov nový zoznam nepozná — zahodí sa, aby neostal v adrese bez účinku.
 */
function LegacyContactRedirect() {
  const { id } = useParams<{ id: string }>()
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  const brokerId = params.get('brokerId')
  if (brokerId) {
    params.delete('brokerId')
    params.set('agentId', brokerId)
  }
  params.delete('customerType')
  const query = params.toString()
  return <Navigate to={`/crm/contacts${id ? `/${id}` : ''}${query ? `?${query}` : ''}`} replace />
}

function AppRoutes() {
  return (
    <Suspense fallback={<LazyFallback />}>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/form/:token" element={
        <Suspense fallback={<LazyFallback />}>
          <PartyFormPage />
        </Suspense>
      } />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/change-password" element={<ChangePasswordPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardRouter />} />
        <Route path="profile" element={<MyProfilePage />} />

        {/* My Deals — TIPER má vlastný read-only pohľad (GET /deals/my) */}
        <Route path="my-deals" element={<RoleRoute roles={STAFF_AND_TIPER}><MyDealsPage /></RoleRoute>} />

        {/* Properties */}
        <Route path="properties" element={<RoleRoute roles={STAFF_ROLES}><PropertiesPage /></RoleRoute>} />
        <Route path="properties/new" element={<RoleRoute roles={STAFF_ROLES}><PropertyFormPage /></RoleRoute>} />
        <Route path="properties/:id/edit" element={<RoleRoute roles={STAFF_ROLES}><PropertyFormPage /></RoleRoute>} />

        {/* CRM — detail obchodu a kontakty sú povolené aj pre TIPERA (API allowlist) */}
        <Route path="crm/deals/:id" element={<RoleRoute roles={STAFF_AND_TIPER}><DealDetailPage /></RoleRoute>} />
        <Route path="crm/pipeline" element={<RoleRoute roles={STAFF_ROLES}><PipelinePage /></RoleRoute>} />
        <Route path="crm/contacts" element={<RoleRoute roles={STAFF_AND_TIPER}><ContactsPage /></RoleRoute>} />
        <Route path="crm/contacts/:id" element={<RoleRoute roles={STAFF_AND_TIPER}><ContactDetailPage /></RoleRoute>} />
        {/* Presmerovanie bez RoleRoute — rolu overí cieľová trasa. */}
        <Route path="crm/leads" element={<LegacyContactRedirect />} />
        <Route path="crm/leads/:id" element={<LegacyContactRedirect />} />
        <Route path="crm/clients" element={<LegacyContactRedirect />} />
        <Route path="crm/clients/:id" element={<LegacyContactRedirect />} />

        {/* Emails — base = Prijaté (INBOX); :folder = sent | drafts | trash */}
        <Route path="emails" element={<RoleRoute roles={STAFF_ROLES}><EmailsPage /></RoleRoute>} />
        <Route path="emails/:folder" element={<RoleRoute roles={STAFF_ROLES}><EmailsPage /></RoleRoute>} />

        {/* Tasks */}
        <Route path="tasks" element={<RoleRoute roles={STAFF_ROLES}><TasksPage /></RoleRoute>} />

        {/* Calendar */}
        <Route path="calendar" element={<RoleRoute roles={STAFF_ROLES}><CalendarPage /></RoleRoute>} />

        {/* Reports */}
        <Route path="reports" element={<RoleRoute roles={MANAGER_PLUS_ROLES}><ReportsPage /></RoleRoute>} />

        {/* Admin */}
        <Route path="users" element={<RoleRoute roles={['ASSISTANT', 'BRANCH_MANAGER', 'OWNER', 'SUPER_ADMIN']}><UsersPage /></RoleRoute>} />
        <Route path="users/:id" element={<RoleRoute roles={MANAGER_PLUS_ROLES}><UserDetailPage /></RoleRoute>} />
        <Route path="branches" element={<RoleRoute roles={MANAGER_PLUS_ROLES}><BranchesPage /></RoleRoute>} />
        <Route path="exports" element={<RoleRoute roles={OWNER_PLUS_ROLES}><ExportCenterPage /></RoleRoute>} />
        <Route path="media" element={<RoleRoute roles={STAFF_ROLES}><MediaLibraryPage /></RoleRoute>} />
        <Route path="settings" element={<RoleRoute roles={['ASSISTANT', 'BRANCH_MANAGER', 'OWNER', 'SUPER_ADMIN']}><SettingsPage /></RoleRoute>} />

        {/* My Web (Broker website) */}
        <Route path="my-web" element={<RoleRoute roles={STAFF_ROLES}><MyWebOverviewPage /></RoleRoute>} />
        <Route path="my-web/settings" element={<RoleRoute roles={STAFF_ROLES}><MyWebSettingsPage /></RoleRoute>} />
        <Route path="my-web/content" element={<RoleRoute roles={STAFF_ROLES}><MyWebContentPage /></RoleRoute>} />
        <Route path="my-web/blog" element={<RoleRoute roles={STAFF_ROLES}><MyWebBlogPage /></RoleRoute>} />
        <Route path="my-web/blog/new" element={<RoleRoute roles={STAFF_ROLES}><MyWebBlogFormPage /></RoleRoute>} />
        <Route path="my-web/blog/:id/edit" element={<RoleRoute roles={STAFF_ROLES}><MyWebBlogFormPage /></RoleRoute>} />
        <Route path="my-web/references" element={<RoleRoute roles={STAFF_ROLES}><MyWebReferencesPage /></RoleRoute>} />

        {/* Email Templates */}
        <Route path="email-templates" element={<RoleRoute roles={STAFF_ROLES}><EmailTemplatesListPage /></RoleRoute>} />
        <Route path="email-templates/new" element={<RoleRoute roles={STAFF_ROLES}><EmailTemplateFormPage /></RoleRoute>} />
        <Route path="email-templates/:id/edit" element={<RoleRoute roles={STAFF_ROLES}><EmailTemplateFormPage /></RoleRoute>} />

        {/* Automation */}
        <Route path="automation" element={<RoleRoute roles={STAFF_ROLES}><AutomationPage /></RoleRoute>} />

        {/* Import — OWNER / SUPER_ADMIN only */}
        <Route path="imports" element={<RoleRoute roles={OWNER_PLUS_ROLES}><ImportPage /></RoleRoute>} />

        {/* Reality Monitor */}
        <Route path="monitor" element={<RoleRoute roles={STAFF_ROLES}><MonitorPage /></RoleRoute>} />

        {/* Tipers — Phase 4 (správa tiperov je staff-only; tiper sám sem nenaviguje) */}
        <Route
          path="tipers"
          element={
            <RoleRoute roles={STAFF_ROLES}>
              <Suspense fallback={<LazyFallback />}>
                <TipersPage />
              </Suspense>
            </RoleRoute>
          }
        />
        <Route
          path="tipers/:id"
          element={
            <RoleRoute roles={STAFF_ROLES}>
              <Suspense fallback={<LazyFallback />}>
                <TiperDetailPage />
              </Suspense>
            </RoleRoute>
          }
        />

        {/* Legal services — Phase 4/5 (LAWYER má /lawyer-requests endpointy v allowliste) */}
        <Route
          path="legal-requests"
          element={
            <RoleRoute roles={STAFF_AND_LAWYER}>
              <Suspense fallback={<LazyFallback />}>
                <LegalRequestsPage />
              </Suspense>
            </RoleRoute>
          }
        />
        <Route
          path="legal-requests/:id"
          element={
            <RoleRoute roles={STAFF_AND_LAWYER}>
              <Suspense fallback={<LazyFallback />}>
                <LegalRequestDetail />
              </Suspense>
            </RoleRoute>
          }
        />

        {/* Notifications — /notifications API je @AllowRoles('TIPER','LAWYER'), bez gate */}
        <Route path="notifications" element={<NotificationsPage />} />

        {/* My Team */}
        <Route path="my-team" element={<RoleRoute roles={STAFF_ROLES}><MyTeamPage /></RoleRoute>} />

        {/* Super Admin — SUPER_ADMIN only */}
        <Route path="super-admin" element={<RoleRoute roles={['SUPER_ADMIN']}><SuperAdminPage /></RoleRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </Suspense>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <ConfirmProvider>
        <AppRoutes />
      </ConfirmProvider>
    </AuthProvider>
  )
}
